import { native } from '@/shared/native'

/**
 * The session token pair.
 *
 * **The refresh token is optional.** `/auth/login/otp` promotes the session
 * step one opened and deliberately returns none — one session must never have
 * two live refresh tokens — so a session with only an access token is normal,
 * not broken. Requiring both here read a completed password login as signed
 * out. The live service also serves no refresh route at all; where one exists
 * (the fixtures) it rotates on use, so the stored value is replaced atomically
 * and a concurrent refresh must never run twice — see `client.ts`.
 */
export interface TokenPair {
  accessToken: string
  refreshToken: string | null
  /** Epoch milliseconds. */
  expiresAt: number
  /**
   * Whether this token is a session or only half of one.
   *
   * A `pending_2fa` or `pending_password` token is a real, signed access token
   * that opens exactly one endpoint — the one collecting the factor still
   * owed. It has to be stored, because that endpoint authenticates with it,
   * and storing it is precisely what made "do we have tokens?" the wrong
   * question for the route guard: half a login rendered the whole app.
   *
   * `'unknown'` covers a pair written by an older build, before this field
   * existed. Those were only ever written for sessions the app treated as
   * live, so they are read back as active rather than logging everyone out on
   * upgrade.
   */
  status: 'pending_2fa' | 'pending_password' | 'active' | 'unknown'
}

const ACCESS_KEY = 'access-token'
const REFRESH_KEY = 'refresh-token'
const EXPIRES_KEY = 'access-expires-at'
const STATUS_KEY = 'session-status'

let cached: TokenPair | null = null
let loaded = false

const listeners = new Set<() => void>()

/**
 * Called whenever the pair in memory changes: loaded, saved or cleared.
 *
 * For a component that must not outlive the session it was mounted under.
 * The bell in the app bar polls the unread count, and a `401` that clears the
 * tokens mid-screen has to stop it at once rather than leave it polling with
 * no bearer until the screen is left.
 */
export function subscribeTokens(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function changed(): void {
  for (const listener of listeners) listener()
}

export async function loadTokens(): Promise<TokenPair | null> {
  if (loaded) return cached
  const [accessToken, refreshToken, expiresAt, status] = await Promise.all([
    native.storage.get(ACCESS_KEY),
    native.storage.get(REFRESH_KEY),
    native.storage.get(EXPIRES_KEY),
    native.storage.get(STATUS_KEY),
  ])
  loaded = true
  cached = accessToken
    ? {
        accessToken,
        refreshToken: refreshToken || null,
        expiresAt: Number(expiresAt ?? 0),
        // A pair stored before this field existed was, by construction, one the
        // app already considered live — so it reads as active, not as a logout.
        status: isStoredStatus(status) ? status : 'active',
      }
    : null
  changed()
  return cached
}

function isStoredStatus(value: string | null): value is TokenPair['status'] {
  return (
    value === 'pending_2fa' ||
    value === 'pending_password' ||
    value === 'active' ||
    value === 'unknown'
  )
}

/** Only an `active` pair is a session. Half a login is not a login. */
export function isSignedIn(pair: TokenPair | null): boolean {
  return pair !== null && pair.status === 'active'
}

export async function saveTokens(pair: TokenPair): Promise<void> {
  cached = pair
  loaded = true
  changed()
  await Promise.all([
    native.storage.set(ACCESS_KEY, pair.accessToken),
    pair.refreshToken === null
      ? native.storage.remove(REFRESH_KEY)
      : native.storage.set(REFRESH_KEY, pair.refreshToken),
    native.storage.set(EXPIRES_KEY, String(pair.expiresAt)),
    native.storage.set(STATUS_KEY, pair.status),
  ])
}

export async function clearTokens(): Promise<void> {
  cached = null
  loaded = true
  changed()
  await Promise.all([
    native.storage.remove(ACCESS_KEY),
    native.storage.remove(REFRESH_KEY),
    native.storage.remove(EXPIRES_KEY),
    native.storage.remove(STATUS_KEY),
  ])
}

export function peekTokens(): TokenPair | null {
  return cached
}

/**
 * Build a pair from an auth response's `expires_in` (seconds).
 *
 * `status` defaults to `active` for the callers that only ever see a finished
 * session — a token refresh, and the mocked recovery flows. The entry flow
 * passes the server's own `session_status`, because that is the whole point of
 * reading it.
 */
export function pairFrom(
  accessToken: string,
  refreshToken: string | null,
  expiresInSeconds: number,
  status: TokenPair['status'] = 'active',
): TokenPair {
  return {
    accessToken,
    refreshToken,
    expiresAt: Date.now() + expiresInSeconds * 1000,
    status,
  }
}
